import React, { useState, useEffect, useMemo } from "react";
import { accountAPI, api } from "services";
import { ProfilePhoto } from "components";
import Creatable from "react-select/creatable";
import { creatableStyle } from "assets/themes";
import { toast } from "sonner";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { useForm, Controller } from "react-hook-form";
import { formFields, defaultValues } from "./config";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { getAllTagsFromAccounts } from "utils";
import { defaultTags } from "utils/tags";

const NewAccount = () => {
  const { write, accounts } = useUniversalStore((state) => state);
  const tags = getAllTagsFromAccounts(accounts);
  const mappedTags = defaultTags.concat(tags.map(tag => ({ value: tag, label: tag })));
  const { closeOffcanvas } = useOffcanvasStore();

  const { control, handleSubmit, setValue, watch, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: 'onTouched'
  });

  const photo = watch('photo');

  // Fetch contacts from API and store them in state
  const [contacts, setContacts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);
  
  const fetchContacts = async () => {
    setIsFetching(true);
    try {
      const res = await api.getContacts();
      if (res && res[0]) {
        const contacts = res[0].get("phonebook") || [];
        setContacts(contacts);
      }
    } catch (error) {
      console.error("Failed to fetch contacts", error);
    } finally {
      setIsFetching(false);
    }
  };

  const contactOptions = useMemo(() => {
    return contacts
      .filter(contact => contact?.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((contact) => ({
        value: contact?.number?.replace(/\s+/g, ""),
        label: contact?.name,
      }));
  }, [contacts]);

  /**
   * This function handles the selection of a contact from a dropdown.
   * If no contact is selected, it clears the 'name' and 'phone' fields.
   * If a contact is selected, it sets the 'name' field to the contact's name.
   * If the selected contact is not a new entry, it sets the 'phone' field to the contact's phone number.
   * 
   * @param {Object} selectedOption - The selected contact option from the dropdown.
   */
  const handleContactSelect = (selectedOption) => {
    if (!selectedOption) {
      setValue('name', '', { shouldValidate: true });
      setValue('phone', '');
      return;
    }

    setValue('name', selectedOption.label, { shouldValidate: true });
    if (!selectedOption.__isNew__) {
      setValue('phone', selectedOption.value || '');
    }
  };

  const onSubmit = async (data) => {
    try {
      const result = await accountAPI.createAccount(
        data.name,
        data.phone,
        data.address,
        data.details,
        data.tag?.value || null,
        data.photo.url
      );

      //Refresh Accounts data in local store
      write({ 
        accounts: await accountAPI.getAccounts(),
        accountId: result.id
      });

      toast.success("Account created successfully");
      closeOffcanvas();
    } catch (error) {
      toast.error("Failed to create account");
    }
  };

  const renderField = (field) => {
    const commonProps = {
      placeholder: field.placeholder,
      className: `form-control ${errors[field.name] ? 'is-invalid' : watch(field.name) ? 'is-valid' : ''}`
    };

    if (field.name === 'name' || field.name === 'tag') {
      return (
        <div className="mb-3" key={field.name}>
          <Controller
            name={field.name}
            control={control}
            rules={field.rules}
            render={({ field: { value, onChange } }) => (
              <>
                {field.name === 'tag' && (
                  <Creatable
                    isClearable
                    options={mappedTags}
                    value={value}
                    onChange={onChange}
                    placeholder={field.placeholder}
                    classNamePrefix="rs"
                    styles={creatableStyle}
                    className={`${errors[field.name] ? 'is-invalid' : value ? 'is-valid' : ''}`}
                  />
                )}
                {field.name === 'name' && (
                  <Creatable
                    options={contactOptions}
                    value={value ? { label: value, value: watch('phone') } : null}
                    classNamePrefix="rs"
                    isLoading={isFetching}
                    isClearable
                    placeholder={field.placeholder}
                    onChange={handleContactSelect}
                    styles={creatableStyle}
                    className={errors[field.name] ? 'is-invalid' : value ? 'is-valid' : ''}
                  />
                )}
                {errors[field.name] && (
                  <div className="invalid-feedback d-block">
                    {errors[field.name].message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      );
    }

    return (
      <div className="mb-3" key={field.name}>
        <Controller
          name={field.name}
          control={control}
          rules={field.rules}
          render={({ field: { ref, ...fieldProps } }) => {
            switch (field.type) {
              case 'phone':
                return (
                  <PhoneInput
                    {...fieldProps}
                    defaultCountry="in"
                    hideDropdown={true}
                    style={{ width: "100%" }}
                    {...commonProps}
                  />
                );
              case 'textarea':
                return (
                  <textarea
                    {...fieldProps}
                    {...commonProps}
                    rows="3"
                  />
                );
              default:
                return (
                  <input
                    {...fieldProps}
                    type={field.type}
                    {...commonProps}
                  />
                );
            }
          }}
        />
        {errors[field.name] && (
          <div className="invalid-feedback">{errors[field.name].message}</div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="my-2" align="center">


        <ProfilePhoto
          key="add-profile-photo"
          url={photo?.url}
          loading={photo?.loading}
          updateField={(field, data) => setValue(field, data)}
        />
      </div>

      {formFields.map(renderField)}

      <br />
      <br />
      <br />

      <div className="row sticky-bottom-drawer mb-3">
        <div className="col-md-12 d-grid">
          <button
            type="submit"
            className="btn btn-app py-3 fs-5 fw-bold text-uppercase"
            disabled={isSubmitting || photo?.loading || !isValid}
          >
            {photo?.loading ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : isSubmitting ? (
              "Adding..."
            ) : (
              "Add"
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default NewAccount;
